import React from "react";
import { useTranslation } from "react-i18next";

import AssetJobSummary from "components/asset-management-system/notification-details/AssetJobSummary";
import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import StartedGreyIcon from "components/icons/StartedGreyIcon";
import style from "components/reports/notification-details/notification-details.scss";
import { AssetImportNotificationType, isAssetImportNotificationType } from "domain/assetImport";
import { parseAssetJobNotificationData } from "domain/assetJobNotification";
import { Notification } from "domain/notification";
import { useTheme } from "utils/useTheme";

import FailedRedNotificationIcon from "assets/images/icons/alert.svg";
import PassedGreenNotificationIcon from "assets/images/icons/checkMarkInCircle.svg";
import WarningYellowNotificationIcon from "assets/images/icons/quickIndeterminate.svg";

import testIds from "testIds.json";

const ICONS: Record<Exclude<AssetImportNotificationType, "ASSET_IMPORT_STARTED">, string> = {
    ASSET_IMPORT_SUCCEEDED: PassedGreenNotificationIcon,
    ASSET_IMPORT_WARNING: WarningYellowNotificationIcon,
    ASSET_IMPORT_FAILED: FailedRedNotificationIcon,
};

interface Props {
    notification: Notification;
}

const AssetImportNotificationDetails = (props: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const notificationType = props.notification.type;
    if (!isAssetImportNotificationType(notificationType)) {
        return null;
    }
    const data = parseAssetJobNotificationData(props.notification.data);

    const details: Record<AssetImportNotificationType, string> = {
        ASSET_IMPORT_STARTED: t("Notification.assetImportNotifications.inProgressDetails"),
        ASSET_IMPORT_SUCCEEDED: t("Notification.assetImportNotifications.successDetails"),
        ASSET_IMPORT_WARNING: t("Notification.assetImportNotifications.warningDetails"),
        ASSET_IMPORT_FAILED: t("Notification.assetImportNotifications.failedDetails"),
    };

    return (
        <div
            data-testid={testIds.workArea.asset.assetImportResultsDialog.itself}
            className={style.detailContainer}
            data-type={notificationType}
        >
            <div className={style.titleMessage}>
                {notificationType === "ASSET_IMPORT_STARTED" ? (
                    <StartedGreyIcon pathColor={theme.contentBackgroundColor} fillColor={theme.textColor} />
                ) : (
                    <img className={style.image} src={ICONS[notificationType]} alt={t("AltText.confirmationIcon")} />
                )}
            </div>
            <div className={style.details} data-testid={testIds.workArea.asset.assetImportResultsDialog.detailsLabel}>
                {details[notificationType]}
                {notificationType === "ASSET_IMPORT_FAILED" && data.jobUuid !== undefined && (
                    <div>
                        <strong>{t("Notification.assetImportNotifications.failedJobUuid")}</strong> {data.jobUuid}
                        <CopyToClipboard value={data.jobUuid} />
                    </div>
                )}
            </div>
            <AssetJobSummary
                groupName={data.groupName}
                totalRequestedAssets={data.totalRequestedAssets}
                totalSucceededAssets={data.totalSucceededAssets}
                totalFailedAssets={data.totalFailedAssets}
            />
        </div>
    );
};

export default AssetImportNotificationDetails;
